/**
 * AERWEAR Meta Pixel + Conversions API tracking
 *
 * Browser Pixel fires: PageView, ViewContent, AddToCart, InitiateCheckout
 * Purchase is handled SERVER-SIDE ONLY via Shopify webhook → /api/meta/order-webhook
 * This prevents double-counting since checkout happens on Shopify's hosted page.
 */
import api from "./api";
import { gpcEnabled } from "./privacy";

// --- Attribution cookie helpers ---

export function getFbp() {
  if (gpcEnabled()) return null;
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match ? match[1] : null;
}

export function getFbc() {
  if (gpcEnabled()) return null; // don't create _fbc from fbclid when GPC is on
  const match = document.cookie.match(/_fbc=([^;]+)/);
  if (match) return match[1];
  // Build _fbc from fbclid — use the RAW value from the URL, no lowercasing/trimming
  const urlMatch = window.location.search.match(/[?&]fbclid=([^&]+)/);
  if (urlMatch) {
    const rawFbclid = urlMatch[1]; // Preserve exactly as-is from URL
    const fbc = `fb.1.${Date.now()}.${rawFbclid}`;
    document.cookie = `_fbc=${fbc}; path=/; max-age=7776000; SameSite=Lax`;
    return fbc;
  }
  return null;
}

// --- Event helpers ---

function genEventId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Send a server-side event to our backend → Meta CAPI
 * This runs in parallel with the browser pixel for deduplication via event_id
 */
function sendServerEvent(eventName, eventId, customData = {}) {
  if (gpcEnabled()) return;
  const payload = {
    event_name: eventName,
    event_id: eventId,
    event_source_url: window.location.href,
    fbp: getFbp(),
    fbc: getFbc(),
    custom_data: customData,
  };
  api.post("/meta/event", payload).catch(() => {});
}

// --- Standard events ---

export function trackPageView() {
  const eventId = genEventId();
  if (window.fbq) window.fbq("track", "PageView", {}, { eventID: eventId });
  sendServerEvent("PageView", eventId);
}

export function trackViewContent(product) {
  const eventId = genEventId();
  const data = {
    content_name: product.name,
    content_ids: [product.id],
    content_type: "product",
    value: product.price,
    currency: "USD",
  };
  if (window.fbq) window.fbq("track", "ViewContent", data, { eventID: eventId });
  sendServerEvent("ViewContent", eventId, data);
}

export function trackAddToCart(product, quantity = 1) {
  const eventId = genEventId();
  const data = {
    content_name: product.name,
    content_ids: [product.id],
    content_type: "product",
    value: product.price * quantity,
    currency: "USD",
    num_items: quantity,
  };
  if (window.fbq) window.fbq("track", "AddToCart", data, { eventID: eventId });
  sendServerEvent("AddToCart", eventId, data);
}

export function trackInitiateCheckout(items, totalValue) {
  const eventId = genEventId();
  const data = {
    content_ids: items.map((i) => i.product_id),
    content_type: "product",
    value: totalValue,
    currency: "USD",
    num_items: items.reduce((s, i) => s + i.quantity, 0),
  };
  if (window.fbq) window.fbq("track", "InitiateCheckout", data, { eventID: eventId });
  sendServerEvent("InitiateCheckout", eventId, data);
}

// NOTE: No trackPurchase — Purchase is handled server-side via Shopify webhook
// to prevent double-counting. See /api/meta/order-webhook
